/**
 * Steuerberater-Auswertung
 * ------------------------
 * Zeigt für einen frei wählbaren Zeitraum: Vereinnahmte Kalt-/NK-/Heizkosten-/
 * Stellplatz-Miete, USt-Anteil, offene Forderungen & Überzahlungen.
 *
 * Gruppierung: je MV / Objekt / Eigentümer. Export: CSV.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Calculator } from "@phosphor-icons/react";
import { LevelPicker, TaxExportButtons, levelParams } from "@/components/reports/TaxExport";

const CATS = [
  { k: "rent_cold", label: "Kaltmiete" },
  { k: "utilities", label: "Betriebskosten-VZ" },
  { k: "heating", label: "Heizkosten-VZ" },
  { k: "parking", label: "Stellplatz" },
  { k: "other", label: "Sonstige Einnahmen" },
];

export default function SteuerberaterReport() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today.slice(0, 4) + "-01-01");
  const [to, setTo] = useState(today);
  const [groupBy, setGroupBy] = useState("tenancy");
  const [level, setLevel] = useState("company");
  const [entity, setEntity] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (level !== "company" && !entity) { toast.error("Bitte zuerst auswählen, wofür ausgewertet werden soll"); return; }
    setLoading(true);
    try {
      const { data: res } = await api.get(`/reports/tax-export`, {
        params: { date_from: from, date_to: to, group_by: groupBy, format: "json", ...levelParams(level, entity) },
      });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    } finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const exportCsv = () => {
    if (!data) return;
    const header = ["Name/MV", "Kaltmiete", "NK-VZ", "Heizkosten-VZ", "Stellplatz", "Sonstiges", "USt-Anteil", "Summe", "Offene Forderung", "Überzahlung"];
    const rows = (data.rows || []).map((r) => [
      r.tenant_name || r.name || r.contract_number || r.id,
      r.rent_cold || 0, r.utilities || 0, r.heating || 0,
      r.parking || 0, r.other || 0, r.vat_amount || 0, r.total || 0,
      r.open_balance || 0, r.overpayment || 0,
    ]);
    const csv = [header, ...rows].map((row) => row.map((v) => typeof v === "number" ? v.toFixed(2).replace(".", ",") : `"${(v || "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Steuerberater_${from}_${to}.csv`;
    a.click();
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="tax-advisor-report">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Calculator size={22} /> Steuerberater-Auswertung
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Vereinnahmte Miete und Nebenkosten je Mietverhältnis, Objekt oder Eigentümer —
            jederzeit auch als vorläufige Auswertung zum aktuellen Buchungsstand.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <TaxExportButtons from={from} to={to} level={level} entity={entity} />
          <Button onClick={exportCsv} disabled={!data?.rows?.length} variant="ghost" className="gap-1.5" data-testid="tax-export">
            <Download size={14} /> CSV
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 bg-white border border-slate-200 rounded-md p-4">
        <LevelPicker level={level} setLevel={setLevel} entity={entity} setEntity={setEntity} />
        <div>
          <Label>Von</Label>
          <DatePicker value={from} onChange={setFrom} data-testid="tax-from" />
        </div>
        <div>
          <Label>Bis</Label>
          <DatePicker value={to} onChange={setTo} data-testid="tax-to" />
        </div>
        <div>
          <Label>Gruppierung</Label>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger data-testid="tax-group"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tenancy">je Mietverhältnis</SelectItem>
              <SelectItem value="property">je Objekt</SelectItem>
              <SelectItem value="owner">je Eigentümer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={run} disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="tax-run">
            {loading ? "Lade…" : "Auswertung erstellen"}
          </Button>
        </div>
      </div>
      {data && (
        <div className="text-sm text-slate-700" data-testid="tax-scope-label">
          <b>{data.level}:</b> {data.label} · Zeitraum {(data.date_from || from).split("-").reverse().join(".")} – {(data.date_to || to).split("-").reverse().join(".")}
        </div>
      )}
      {data && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left text-xs text-slate-600">
                <th className="px-2 py-2">Name</th>
                {CATS.map((c) => <th key={c.k} className="px-2 py-2 text-right">{c.label}</th>)}
                <th className="px-2 py-2 text-right">USt</th>
                <th className="px-2 py-2 text-right">Summe</th>
                <th className="px-2 py-2 text-right">Rückstand</th>
                <th className="px-2 py-2 text-right">Überz.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data.rows || []).map((r) => (
                <tr key={r.id} data-testid={`tax-row-${r.id}`}>
                  <td className="px-2 py-1.5 font-medium">
                    {r.tenant_name || r.name || r.contract_number || r.id}
                    {r.property_name && <span className="text-xs text-slate-500 ml-1">· {r.property_name}</span>}
                  </td>
                  {CATS.map((c) => <td key={c.k} className="px-2 py-1.5 text-right tabular-nums">{(r[c.k] || 0).toFixed(2)}</td>)}
                  <td className="px-2 py-1.5 text-right tabular-nums">{(r.vat_amount || 0).toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{(r.total || 0).toFixed(2)}</td>
                  <td className={`px-2 py-1.5 text-right tabular-nums ${r.open_balance > 0 ? "text-rose-700" : ""}`}>{(r.open_balance || 0).toFixed(2)}</td>
                  <td className={`px-2 py-1.5 text-right tabular-nums ${r.overpayment > 0 ? "text-emerald-700" : ""}`}>{(r.overpayment || 0).toFixed(2)}</td>
                </tr>
              ))}
              {(!data.rows || !data.rows.length) && (
                <tr><td colSpan={10} className="px-2 py-8 text-center text-slate-500">Keine Daten im Zeitraum</td></tr>
              )}
            </tbody>
            <tfoot className="bg-slate-50 border-t font-semibold">
              <tr>
                <td className="px-2 py-2">Gesamtsumme</td>
                {CATS.map((c) => <td key={c.k} className="px-2 py-2 text-right tabular-nums">{(data.sums?.[c.k] || 0).toFixed(2)}</td>)}
                <td className="px-2 py-2 text-right tabular-nums">{(data.sums?.vat_amount || 0).toFixed(2)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{(data.sums?.total || 0).toFixed(2)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{(data.sums?.open_balance || 0).toFixed(2)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{(data.sums?.overpayment || 0).toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
